import { useParams, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { FaArrowLeft } from 'react-icons/fa6';
import { GoUpload } from 'react-icons/go';
import { QuizSettings } from '@/components/quiz/QuizSettings';
import { QuizStats } from '@/components/quiz/QuizStats';
import { QuizQuestions } from '@/components/quiz/QuizQuestions';
import { CustomButton } from '@/components/uiWrappers/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { PublishQuizModal } from '@/components/quiz/PublishQuizModal';
import { UniversitySelectDialog } from '@/components/admin/UniversitySelectDialog';
import { College } from '@mightybyte/business';

export function QuizEditScreen() {
  const { quizId } = useParams({ from: '/admin/quizzes/$quizId' });
  const navigate = useNavigate();
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading, error} = trpc.admin.quiz.getById.useQuery({ id: quizId });
  const trpcUtils = trpc.useUtils();

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (error || !data?.quiz) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Module Not Found</h2>
          <p className="text-gray-600 mb-6">{error?.message || 'The quiz you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }

  const quiz = data.quiz;

  const handleExportQuizData = async (college?: College) => {
    setIsExporting(true);
    setIsExportDialogOpen(false);

    try {
      const csvData = await trpcUtils.admin.quiz.exportQuizDataCSV.fetch({
        quizId: quiz._id,
        institution: college,
      });

      if (csvData) {
        // Create timestamp for filename
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
        const fileName = college
          ? `quiz_${quiz.number}_${college}_${timestamp}.csv`
          : `quiz_${quiz.number}_all_${timestamp}.csv`;

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export quiz data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <CustomButton
            variant="ghost"
            onClick={() => navigate({ to: '/admin/quizzes' })}
            >
          <FaArrowLeft className="w-5 h-5 mr-2/clear" />
          </CustomButton>
          <h1 className="text-3xl font-bold text-gray-900">Module {quiz.number}</h1>
          <Badge className={`ml-4 ${quiz.isDraft ? 'bg-yellow-100' : 'bg-brand'}`}
                 variant={quiz.isDraft ? 'secondary' : 'default'}
          >
            {quiz.isDraft ? 'Draft' : 'Published'}</Badge>
        </div>

        <div className="flex items-center gap-3">
          {/* Export Quiz Data Button - Only for Published Quizzes */}
          {!quiz.isDraft && (
            <CustomButton
              type="button"
              onClick={() => setIsExportDialogOpen(true)}
              disabled={isExporting}
              variant="outline"
              size="lg"
              className="rounded-lg flex items-center gap-2"
            >
              <GoUpload className="size-5 text-primary" data-icon="inline-start" />
              {isExporting ? 'Exporting...' : 'Export Data'}
            </CustomButton>
          )}

          {/* Publish Quiz Button - Only for Draft Quizzes */}
          {quiz.isDraft && (
            <CustomButton
              variant={'default'}
              onClick={() => setIsPublishModalOpen(true)}
            >
              Publish Quiz
            </CustomButton>
          )}
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-6">
        {/* Stats Section - Only for Published Quizzes */}
        {!quiz.isDraft && (
            <QuizStats quizId={quiz._id} />
        )}
        {/* Settings Section */}
        <QuizSettings quizId={quiz._id} quizNumber={quiz.number}/>

        {/* Questions Section */}
        <QuizQuestions quizId={quiz._id} quizNumber={quiz.number} questions={quiz.questions} />
      </div>

      {/* Publish Quiz Modal */}
      <PublishQuizModal
        isOpen={isPublishModalOpen}
        onClose={() => setIsPublishModalOpen(false)}
        quizId={quiz._id}
        quizNumber={quiz.number}
      />

      {/* Export Data Modal */}
      <UniversitySelectDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        onSelect={handleExportQuizData}
        isLoading={isExporting}
        title="Export Quiz Data"
        description="Select which institution's data to export"
        allLabel="All Institutions"
      />
    </div>
  );
}
